import React, { useState, useEffect } from 'react';
import {
  Typography,
  Row,
  Col,
  Card,
  Spin,
  Alert,
  Form,
  Upload,
  Button,
  Input,
  message,
  Space,
  Modal,
  Select,
  Switch,
  ColorPicker,
} from 'antd';
import {
  AppstoreOutlined,
  CalendarOutlined,
  DashOutlined,
  EnvironmentOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { UploadOutlined } from '@ant-design/icons';
import { getDashboardCounts } from '../services/adminService';

import { uploadImage } from '../services/uploadService';
import { getCurrentDestination } from '../services/destinationsService';
import { updateCity } from '../services/citiesService';
import { BiChart } from 'react-icons/bi';

const { Title, Text } = Typography;
const { Option } = Select;

function AdminDashboard() {
  const navigate = useNavigate();
  const [currentEmbed, setCurrentEmbed] = useState(null);
  const [embedConfig, setEmbedConfig] = useState({
    width: '100%',
    height: '600',
    border: '0',
    borderRadius: '0',
    padding: '24',
    layout: 'grid',
    theme: {
      background: '#ffffff',
      eventBackground: '#ffffff',
      accent: '#1890ff',
      text: '#000000',
      pageBackground: '#ffffff',
    },
    showDescription: true,
    scrollDirection: 'horizontal',
    cardBorderWidth: 1,
  });
  const [counts, setCounts] = useState({
    places: 0,
    events: 0,
    tours: 0,
  });
  const [destination, setDestination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);

  const [form] = Form.useForm();

  const paths = {
    grid: '/events/embed',
    calendar: '/events/calendar',
    widget: '/events/widget',
    categories: '/categories/embed',
    places: '/places/embed',
    calendarEmbed: '/calendar/embed',
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [countsData, destinationData] = await Promise.all([
        getDashboardCounts(),
        getCurrentDestination(),
      ]);
      setCounts(countsData);
      setDestination(destinationData);
      if (destinationData) {
        form.setFieldsValue({
          name: destinationData.name,
          description: destinationData.description,
          image_url: destinationData.image_url,
          state: destinationData.state,
          country: destinationData.country,
          population: destinationData.population,
          timezone: destinationData.timezone,
          website_url: destinationData.website_url,
          lat: destinationData.lat,
          lon: destinationData.lon,
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert message={error} type="error" />
      </div>
    );
  }

  const handleSubmit = async values => {
    try {
      await updateCity(destination.id, values);
      message.success('Hero section updated successfully');
      fetchData();
    } catch (err) {
      message.error('Failed to update hero section');
    }
  };

  return (
    <div>
      {/* Hero Preview Section */}
      <div
        style={{
          position: 'relative',
          height: '300px',
          backgroundImage: `url(${
            destination?.image_url || 'https://source.unsplash.com/random?city'
          })`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          marginBottom: '24px',
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            color: 'white',
          }}
        >
          <Title level={2} style={{ color: 'white', margin: 0 }}>
            {destination?.name}
          </Title>
          <Text style={{ color: 'white' }}>{destination?.description}</Text>
          {destination?.lat && destination?.lon && (
            <Text style={{ color: 'white' }}>
              {destination?.lat}, {destination?.lon}{' '}
            </Text>
          )}
          <Button
            type="primary"
            onClick={() => setEditModalVisible(true)}
            style={{ position: 'absolute', top: 24, right: 24 }}
          >
            Edit Hero Section
          </Button>
        </div>
      </div>

      <div style={{ padding: '24px' }}>
        {destination?.active !== true && (
          <Alert
            message="City Not Active"
            description={
              <span>
                This city is not currently active. Please visit the{' '}
                <Button
                  type="link"
                  onClick={() => navigate('/admin/billing')}
                  style={{ padding: 0 }}
                >
                  billing page
                </Button>{' '}
                to activate your subscription.
              </span>
            }
            type="warning"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}
        <Row gutter={[24, 24]}>
          {Object.entries(counts).map(([key, value]) => (
            <Col xs={24} sm={12} md={6} key={key}>
              <Card>
                <div style={{ textAlign: 'center' }}>
                  <Title level={2} style={{ margin: 0 }}>
                    {value}
                  </Title>
                  <Typography.Text type="secondary">
                    Total {key.charAt(0).toUpperCase() + key.slice(1)}
                  </Typography.Text>
                </div>
              </Card>
            </Col>
          ))}
          {destination.dashboard_url && (
            <Col xs={24} sm={12} md={6}>
              <Card
                onClick={() => {
                  // open tab
                  destination &&
                    destination.dashboard_url &&
                    window.open(destination.dashboard_url, '_blank');
                }}
                hoverable
              >
                <div style={{ textAlign: 'center' }}>
                  <Title level={2} style={{ margin: 0 }}>
                    <BiChart color={destination && destination.dashboard_url ? 'black' : 'grey'} />
                  </Title>
                  {destination && destination.dashboard_url ? (
                    <Typography.Text type="secondary">App Usage</Typography.Text>
                  ) : (
                    <Typography.Text type="secondary">App Usage Not Setup</Typography.Text>
                  )}
                </div>
              </Card>
            </Col>
          )}
        </Row>
        <div style={{ marginBottom: 48 }}>
          <Title level={3}>Embeds</Title>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card
                size="small"
                hoverable
                onClick={() => setCurrentEmbed('grid')}
                style={{ cursor: 'pointer' }}
              >
                <Space align="start">
                  <AppstoreOutlined style={{ fontSize: 24 }} />
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      Events Grid
                    </Title>
                    <div type="secondary">
                      A responsive grid of upcoming events with search functionality.
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card
                size="small"
                hoverable
                onClick={() => setCurrentEmbed('calendar')}
                style={{ cursor: 'pointer' }}
              >
                <Space align="start">
                  <CalendarOutlined style={{ fontSize: 24 }} />
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      Events Calendar
                    </Title>
                    <div type="secondary">A calendar view showing all your events.</div>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card
                size="small"
                hoverable
                onClick={() => setCurrentEmbed('widget')}
                style={{ cursor: 'pointer' }}
              >
                <Space align="start">
                  <UnorderedListOutlined style={{ fontSize: 24 }} />
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      Events Widget
                    </Title>
                    <div type="secondary">
                      A compact widget showing your next 7 upcoming events.
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card
                size="small"
                hoverable
                onClick={() => setCurrentEmbed('categories')}
                style={{ cursor: 'pointer' }}
              >
                <Space align="start">
                  <AppstoreOutlined style={{ fontSize: 24 }} />
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      Categories Grid
                    </Title>
                    <div type="secondary">
                      A responsive grid of categories with optional filtering.
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card
                size="small"
                hoverable
                onClick={() => setCurrentEmbed('places')}
                style={{ cursor: 'pointer' }}
              >
                <Space align="start">
                  <EnvironmentOutlined style={{ fontSize: 24 }} />
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      Places Grid
                    </Title>
                    <div type="secondary">
                      A responsive grid of places with search and category filtering.
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card
                size="small"
                hoverable
                onClick={() => setCurrentEmbed('calendarEmbed')}
                style={{ cursor: 'pointer' }}
              >
                <Space align="start">
                  <CalendarOutlined style={{ fontSize: 24 }} />
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      Custom Calendar
                    </Title>
                    <div type="secondary">
                      A customizable calendar widget with saveable layouts and themes.
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>

          <Modal
            title="Embed Details"
            open={!!currentEmbed}
            onCancel={() => setCurrentEmbed(null)}
            footer={[]}
          >
            {currentEmbed && (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Title level={5}>Instructions</Title>
                <div>
                  Copy and paste this code into your website where you want the embed to appear:
                </div>
                <Form layout="vertical">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Width">
                        <Input
                          value={embedConfig.width}
                          onChange={e =>
                            setEmbedConfig({
                              ...embedConfig,
                              width: e.target.value,
                            })
                          }
                          placeholder="100%"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Height">
                        <Input
                          value={embedConfig.height}
                          onChange={e =>
                            setEmbedConfig({
                              ...embedConfig,
                              height: e.target.value,
                            })
                          }
                          placeholder="600"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Border Width">
                        <Input
                          value={embedConfig.border}
                          onChange={e =>
                            setEmbedConfig({
                              ...embedConfig,
                              border: e.target.value,
                            })
                          }
                          placeholder="0"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Border Radius">
                        <Input
                          value={embedConfig.borderRadius}
                          onChange={e =>
                            setEmbedConfig({
                              ...embedConfig,
                              borderRadius: e.target.value,
                            })
                          }
                          placeholder="0"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Padding">
                        <Input
                          value={embedConfig.padding}
                          onChange={e =>
                            setEmbedConfig({
                              ...embedConfig,
                              padding: e.target.value,
                            })
                          }
                          placeholder="24"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Calendar-specific configuration options */}
                  {currentEmbed === 'calendarEmbed' && (
                    <>
                      <Title level={5}>Calendar Settings</Title>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item label="Layout">
                            <Select
                              value={embedConfig.layout}
                              onChange={layout =>
                                setEmbedConfig({
                                  ...embedConfig,
                                  layout,
                                })
                              }
                            >
                              <Option value="grid">Grid</Option>
                              <Option value="list">List</Option>
                              <Option value="agenda">Agenda</Option>
                              <Option value="slider">Slider</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label="Accent Color">
                            <ColorPicker
                              value={embedConfig.theme.accent}
                              onChange={color =>
                                setEmbedConfig({
                                  ...embedConfig,
                                  theme: {
                                    ...embedConfig.theme,
                                    accent: color.toHexString(),
                                  },
                                })
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label="Page Background">
                            <ColorPicker
                              value={embedConfig.theme.pageBackground}
                              onChange={color =>
                                setEmbedConfig({
                                  ...embedConfig,
                                  theme: {
                                    ...embedConfig.theme,
                                    pageBackground: color.toHexString(),
                                  },
                                })
                              }
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item label="Show Descriptions">
                            <Switch
                              checked={embedConfig.showDescription}
                              onChange={checked =>
                                setEmbedConfig({
                                  ...embedConfig,
                                  showDescription: checked,
                                })
                              }
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item label="Card Border Width (px)">
                            <Input
                              type="number"
                              min={0}
                              max={10}
                              value={embedConfig.cardBorderWidth}
                              onChange={e =>
                                setEmbedConfig({
                                  ...embedConfig,
                                  cardBorderWidth: parseInt(e.target.value) || 0,
                                })
                              }
                            />
                          </Form.Item>
                        </Col>
                        {embedConfig.layout === 'grid' && (
                          <Col span={8}>
                            <Form.Item label="Scroll Direction">
                              <Select
                                value={embedConfig.scrollDirection}
                                onChange={direction =>
                                  setEmbedConfig({
                                    ...embedConfig,
                                    scrollDirection: direction,
                                  })
                                }
                              >
                                <Option value="horizontal">Horizontal</Option>
                                <Option value="vertical">Vertical</Option>
                              </Select>
                            </Form.Item>
                          </Col>
                        )}
                      </Row>
                    </>
                  )}
                </Form>

                <div style={{ position: 'relative', marginTop: 6 }}>
                  <Input.TextArea
                    readOnly
                    rows={6}
                    value={`<iframe
  src="${window.location.origin}${paths[currentEmbed]}${
    currentEmbed === 'calendarEmbed'
      ? `?layout=${embedConfig.layout}&theme=${encodeURIComponent(
          JSON.stringify(embedConfig.theme)
        )}&showDescription=${embedConfig.showDescription.toString()}&scrollDirection=${
          embedConfig.scrollDirection
        }&cardBorderWidth=${embedConfig.cardBorderWidth}`
      : ''
  }"
  width="${embedConfig.width}"
  height="${embedConfig.height}"
  style="border: ${embedConfig.border}px solid; border-radius: ${embedConfig.borderRadius}px; padding: ${embedConfig.padding}px"
  frameborder="0"
></iframe>`}
                  />
                  <Button
                    type="text"
                    style={{ position: 'absolute', right: 8, bottom: 8 }}
                    onClick={() => {
                      navigator.clipboard.writeText(`<iframe
  src="${window.location.origin}${paths[currentEmbed]}${
    currentEmbed === 'calendarEmbed'
      ? `?layout=${embedConfig.layout}&theme=${encodeURIComponent(
          JSON.stringify(embedConfig.theme)
        )}&showDescription=${embedConfig.showDescription.toString()}&scrollDirection=${
          embedConfig.scrollDirection
        }&cardBorderWidth=${embedConfig.cardBorderWidth}`
      : ''
  }"
  width="${embedConfig.width}"
  height="${embedConfig.height}"
  style="border: ${embedConfig.border}px solid; border-radius: ${embedConfig.borderRadius}px; padding: ${embedConfig.padding}px"
  frameborder="0"
></iframe>`);
                      message.success('Embed code copied to clipboard!');
                    }}
                  >
                    Copy
                  </Button>
                </div>
                <Title level={5} style={{ marginTop: 6, marginBottom: -14 }}>
                  Features
                </Title>
                {currentEmbed === 'grid' && (
                  <ul>
                    <li>Responsive grid layout</li>
                    <li>Search functionality</li>
                    <li>Click events to view ticket links</li>
                    <li>Automatic date formatting</li>
                  </ul>
                )}
                {currentEmbed === 'calendar' && (
                  <ul>
                    <li>Monthly calendar view</li>
                    <li>Event indicators on dates</li>
                    <li>Click events to view details</li>
                    <li>Month navigation</li>
                  </ul>
                )}
                {currentEmbed === 'widget' && (
                  <ul>
                    <li>Compact list view</li>
                    <li>Shows next 7 upcoming events</li>
                    <li>Click events to view ticket links</li>
                    <li>Perfect for sidebars</li>
                  </ul>
                )}
                {currentEmbed === 'categories' && (
                  <ul>
                    <li>Responsive grid layout</li>
                    <li>Filter by category ID</li>
                    <li>Click to view places in category</li>
                    <li>Supports teaser images</li>
                  </ul>
                )}
                {currentEmbed === 'places' && (
                  <ul>
                    <li>Responsive grid layout</li>
                    <li>Search functionality</li>
                    <li>Filter by category</li>
                    <li>Click to view place details</li>
                  </ul>
                )}
                {currentEmbed === 'calendarEmbed' && (
                  <ul>
                    <li>Customizable layout (grid/list/agenda)</li>
                    <li>Theme customization</li>
                    <li>Save and load custom layouts</li>
                    <li>Responsive design</li>
                  </ul>
                )}
              </Space>
            )}
          </Modal>
        </div>

        <div style={{ marginBottom: 48 }}>
          <Title level={3}>Views</Title>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card
                size="small"
                hoverable
                onClick={() => navigate('/kiosk')}
                style={{ cursor: 'pointer' }}
              >
                <Space align="start">
                  <CalendarOutlined style={{ fontSize: 24 }} />
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      Weather Kiosk
                    </Title>
                    <div type="secondary">
                      A full-screen kiosk view showing events and weather information.
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card
                size="small"
                hoverable
                onClick={() => navigate('/app')}
                style={{ cursor: 'pointer' }}
              >
                <Space align="start">
                  <CalendarOutlined style={{ fontSize: 24 }} />
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      App Download
                    </Title>
                    <div type="secondary">
                      A page with links to download your mobile app from app stores.
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card
                size="small"
                hoverable
                onClick={() => navigate('/submit')}
                style={{ cursor: 'pointer' }}
              >
                <Space align="start">
                  <CalendarOutlined style={{ fontSize: 24 }} />
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      Submit Something
                    </Title>
                    <div type="secondary">
                      A form for community members to submit places or events.
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </div>

        <Modal
          title="Edit Hero Section"
          open={editModalVisible}
          onCancel={() => setEditModalVisible(false)}
          footer={null}
          width={800}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={values => {
              handleSubmit(values);
              setEditModalVisible(false);
            }}
          >
            <Form.Item
              name="name"
              label="City Name"
              rules={[{ required: true, message: 'Please input the city name!' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              hidden
              name="state"
              label="State"
              rules={[{ required: false, message: 'Please input the state!' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input.TextArea rows={3} />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="lat"
                  label="Latitude"
                  rules={[{ required: true, message: 'Please input the latitude!' }]}
                >
                  <Input type="text" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="lon"
                  label="Longitude"
                  rules={[{ required: true, message: 'Please input the longitude!' }]}
                >
                  <Input type="text" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="image_url" label="Hero Image">
              <Upload
                maxCount={1}
                accept="image/*"
                beforeUpload={async file => {
                  try {
                    const url = await uploadImage(file);
                    form.setFieldsValue({ image_url: url });
                    message.success('Image uploaded successfully');
                  } catch (err) {
                    message.error('Failed to upload image');
                  }
                  return false;
                }}
              >
                <Button icon={<UploadOutlined />}>Upload Image</Button>
              </Upload>
              {form.getFieldValue('image_url') && (
                <img
                  src={form.getFieldValue('image_url')}
                  alt="Preview"
                  style={{
                    marginTop: 8,
                    maxWidth: '100%',
                    maxHeight: 200,
                    objectFit: 'contain',
                  }}
                />
              )}
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setEditModalVisible(false)}>Cancel</Button>
                <Button type="primary" htmlType="submit">
                  Update Hero Section
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
}

export default AdminDashboard;
